import React from 'react';
import { Box, Typography, LinearProgress, Button, Paper } from '@mui/material';
import { Link } from 'react-router-dom';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { t } from '../../../utils/textUtils';
import { getDaysRemaining, getDeadlineStatus, formatDate } from '../utils/formatters';

const getIconComponent = (iconName) => {
  switch (iconName) {
    case 'Error': return <ErrorIcon />;
    case 'Warning': return <WarningAmberIcon />;
    case 'CheckCircle': return <CheckCircleIcon />;
    case 'Calendar': return <CalendarTodayIcon />;
    default: return <CalendarTodayIcon />;
  }
};

const UpcomingDeadlines = ({ 
  upcomingDeadlines, 
  dashboardOverviewData, 
  projects 
}) => {
  const deadlines = dashboardOverviewData.upcomingDeadlines || upcomingDeadlines;

  return (
    <Paper
      variant="outlined"
      sx={{ 
        p: 3, 
        borderRadius: 2, 
        height: '100%',
        minHeight: '350px',
        borderColor: 'divider',
        bgcolor: '#ffffff'
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 1
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a' }}>
          {t('JADWAL AKAN DATANG')}
        </Typography>
        <Button 
          size="small" 
          component={Link} 
          to="/tasks"
          endIcon={<ArrowForwardIosIcon fontSize="small" />}
          sx={{ 
            color: '#0f172a',
            '&:hover': { bgcolor: '#F8FAFC' }
          }}
        >
          {t('LIHAT SEMUA')}
        </Button>
      </Box>
      
      {deadlines.length > 0 ? (
        deadlines.slice(0, 1).map((deadline) => {
          const daysRemaining = getDaysRemaining(deadline.endDate);
          const deadlineStatus = getDeadlineStatus(daysRemaining, deadline.progress || 0);
          const projectName = deadline.projectName || 
            (deadline.projectId ? projects.find(p => p.id === deadline.projectId)?.name : '');
          const taskName = projectName ? 
            `${deadline.name} (${projectName})` : deadline.name;
            
          return (
            <Box key={deadline.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: '50%', 
                  bgcolor: deadlineStatus.color, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mr: 1.5
                }}>
                  {getIconComponent(deadlineStatus.iconName)}
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {taskName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    {formatDate(deadline.endDate)}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('WAKTU SISA')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: deadlineStatus.color }}>
                    {daysRemaining < 0 ? t('OVERDUE') : `${daysRemaining} ${t('DAYS LEFT')}`}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.max(0, Math.min(100, (deadline.progress || 0)))} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    bgcolor: 'rgba(0,0,0,0.05)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: deadlineStatus.color
                    }
                  }} 
                />
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button 
                  variant="outlined"
                  size="small" 
                  sx={{ 
                    borderColor: deadlineStatus.color,
                    color: deadlineStatus.color,
                    '&:hover': { 
                      borderColor: deadlineStatus.color,
                      bgcolor: `${deadlineStatus.color}10`
                    }
                  }}
                >
                  {t('LIHAT DETAIL')}
                </Button>
              </Box>
            </Box>
          );
        })
      ) : (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('TIDAK ADA JADWAL AKAN DATANG DITEMUKAN')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default UpcomingDeadlines;
